import { tenantAuthStore } from '@/modules/tenant/state/tenantAuth';
import type { EditableBoat } from './boat-changes';

/**
 * #1177 (spec §3.5, decision D35) — a save's pending changes are written here
 * before the request goes out. After an expired session (the redirect to login
 * discards the page) they are restored the next time the same user opens the
 * same boat on the same surface. Entries are keyed by tenant, user, surface and
 * boat, so a colleague on a shared computer never sees them. They are removed
 * when the save lands or fails for another reason (useBoatSave), on Cancel, on a
 * deliberate logout and when a different user signs in (purgePendingBoatChanges)
 * — never by the dead-session redirect. The canvas and the boat page hold
 * different shapes of EditableBoat, hence the surface in the key (review I5).
 * localStorage may be unavailable (private mode, full): every call is best-effort.
 */
export type PendingSurface = 'canvas' | 'detail';

export interface PendingBoatChanges {
  base: EditableBoat;
  current: EditableBoat;
  savedAt: string;
}

const KEY_PREFIX = 'boat-pending-changes:';

interface Identity {
  tenantId: string;
  employeeId: string;
}

/**
 * The user entries are written for. The dead-session handlers (tenantApi's
 * onSessionDead and 401 interceptor) log the store out *before* the page
 * unloads, so the canvas's `beforeunload` write would find nobody signed in
 * (re-review N4). The last signed-in identity is remembered instead; a
 * deliberate logout forgets it (purgePendingBoatChanges()), so nothing is
 * written after one. Module state: a page load starts from the store again.
 */
let lastIdentity: Identity | null = null;

function remember(state: { tenant: { id: string } | null; employee: { id: string } | null }): void {
  if (state.tenant?.id && state.employee?.id) {
    lastIdentity = { tenantId: state.tenant.id, employeeId: state.employee.id };
  }
}
remember(tenantAuthStore.getState());
tenantAuthStore.subscribe(remember);

function keyFor(surface: PendingSurface, boatId: string): string | null {
  remember(tenantAuthStore.getState());
  if (!lastIdentity) return null;
  return `${KEY_PREFIX}${lastIdentity.tenantId}:${lastIdentity.employeeId}:${surface}:${boatId}`;
}

export function readPendingChanges(surface: PendingSurface, boatId: string): PendingBoatChanges | null {
  try {
    const key = keyFor(surface, boatId);
    const raw = key ? localStorage.getItem(key) : null;
    return raw ? (JSON.parse(raw) as PendingBoatChanges) : null;
  } catch {
    return null;
  }
}

export function writePendingChanges(surface: PendingSurface, boatId: string, entry: PendingBoatChanges): void {
  try {
    const key = keyFor(surface, boatId);
    if (key) localStorage.setItem(key, JSON.stringify(entry));
  } catch {
    // best-effort
  }
}

export function clearPendingChanges(surface: PendingSurface, boatId: string): void {
  try {
    const key = keyFor(surface, boatId);
    if (key) localStorage.removeItem(key);
  } catch {
    // best-effort
  }
}

/**
 * Remove stored entries: every one (a deliberate logout), or every one that is
 * not `keep`'s (a sign-in: another user's unsaved edits never reach this one).
 */
export function purgePendingBoatChanges(keep?: { tenantId: string; employeeId: string }): void {
  // A deliberate logout forgets who wrote last; a sign-in makes it the signed-in user.
  lastIdentity = keep ?? null;
  try {
    const keepPrefix = keep ? `${KEY_PREFIX}${keep.tenantId}:${keep.employeeId}:` : null;
    const doomed: string[] = [];
    for (let index = 0; index < localStorage.length; index += 1) {
      const key = localStorage.key(index);
      if (key?.startsWith(KEY_PREFIX) && !(keepPrefix && key.startsWith(keepPrefix))) doomed.push(key);
    }
    for (const key of doomed) localStorage.removeItem(key);
  } catch {
    // best-effort
  }
}
